/**
 * Export Utilities for CSV and Excel
 * Handles data export in various formats
 */

import { V2ChartData } from '../services/V2DataService';

// Import analytical types from chart container
interface StatisticalMetrics {
  mean: number;
  median: number;
  min: number;
  max: number;
  stdDev: number;
  variance: number;
  totalGrowth: number;
  avgGrowthRate: number;
  volatilityScore: number;
}

interface TrendAnalysis {
  sma: number[];
  ema: number[];
  direction: 'bullish' | 'bearish' | 'sideways';
  strength: number;
  momentum: number;
  prediction: {
    nextPeriod: number;
    confidence: number;
    direction: 'up' | 'down' | 'stable';
  };
  seasonality: {
    detected: boolean;
    pattern: string;
    confidence: number;
  };
}

interface AggregatedDataPoint {
  label: string;
  period: string;
  data2024: number;
  data2025: number;
  dealCounts2024?: number;
  dealCounts2025?: number;
}

interface AdvancedFilters {
  valueRange: { enabled: boolean; min: number; max: number };
  growthFilter: { enabled: boolean; type: 'positive' | 'negative' | 'any'; threshold: number };
  dealVolumeFilter: { enabled: boolean; minDeals: number; maxDeals: number };
  dataQuality: { enabled: boolean; requireBothYears: boolean; minValue: number };
}

interface ExportData {
  headers: string[];
  rows: (string | number)[][];
}

/**
 * Convert chart data to CSV format
 */
export function chartDataToCSV(chartData: V2ChartData): string {
  const headers = ['Period', ...chartData.series.map(s => s.name)];
  const rows: (string | number)[][] = [];

  // Add data rows
  chartData.x_axis.forEach((label, index) => {
    const row: (string | number)[] = [label];
    chartData.series.forEach(series => {
      row.push(series.data[index] || 0);
    });
    rows.push(row);
  });

  // Add totals row
  const totalsRow: (string | number)[] = ['Total'];
  chartData.series.forEach(series => {
    const total = series.data.reduce((sum, val) => sum + val, 0);
    totalsRow.push(total);
  });
  rows.push(totalsRow);

  return convertToCSV({ headers, rows });
}

/**
 * Convert funding data with deal counts to CSV
 */
export function fundingDataToCSV(chartData: V2ChartData): string {
  const headers: string[] = ['Period'];

  // Add headers for funding and deal counts
  chartData.series.forEach(series => {
    headers.push(`${series.name} Funding`);
    if (series.deal_counts) {
      headers.push(`${series.name} Deals`);
    }
  });

  const rows: (string | number)[][] = [];

  // Add data rows
  chartData.x_axis.forEach((label, index) => {
    const row: (string | number)[] = [label];
    chartData.series.forEach(series => {
      row.push(formatCurrency(series.data[index] || 0));
      if (series.deal_counts) {
        row.push(series.deal_counts[index] || 0);
      }
    });
    rows.push(row);
  });

  // Add totals row
  const totalsRow: (string | number)[] = ['Total'];
  chartData.series.forEach(series => {
    const totalFunding = series.data.reduce((sum, val) => sum + val, 0);
    totalsRow.push(formatCurrency(totalFunding));
    if (series.deal_counts) {
      const totalDeals = series.deal_counts.reduce((sum, val) => sum + val, 0);
      totalsRow.push(totalDeals);
    }
  });
  rows.push(totalsRow);

  // Add YoY comparison if we have two years
  if (chartData.series.length === 2) {
    const yoyRow: (string | number)[] = ['YoY Change'];
    // Find series by name (not index, to support filters)
    const series2024 = chartData.series.find(s => s.name === '2024');
    const series2025 = chartData.series.find(s => s.name === '2025');

    if (series2024 && series2025) {
      const total2024 = series2024.data.reduce((sum, val) => sum + val, 0);
      const total2025 = series2025.data.reduce((sum, val) => sum + val, 0);
      const yoyChange = total2024 > 0 ? ((total2025 - total2024) / total2024) * 100 : 0;
      yoyRow.push('');
      if (series2024.deal_counts) {
        yoyRow.push('');
      }
      yoyRow.push(`${yoyChange.toFixed(1)}%`);
      if (series2025.deal_counts) {
        yoyRow.push('');
      }
      rows.push(yoyRow);
    }
  }

  return convertToCSV({ headers, rows });
}

/**
 * Convert country funding data to CSV
 */
export function countryFundingToCSV(countryData: any): string {
  if (!countryData || !countryData.countries) {
    return '';
  }

  const headers = ['Country', 'Funding Amount', 'Deal Count', 'Percentage'];
  const rows: (string | number)[][] = [];

  const totalFunding = countryData.countries.reduce(
    (sum: number, c: any) => sum + c.total_funding,
    0
  );

  countryData.countries.forEach((country: any) => {
    const percentage = ((country.total_funding / totalFunding) * 100).toFixed(1);
    rows.push([
      country.name,
      formatCurrency(country.total_funding),
      country.deal_count,
      `${percentage}%`
    ]);
  });

  // Add total row
  rows.push([
    'Total',
    formatCurrency(totalFunding),
    countryData.countries.reduce((sum: number, c: any) => sum + c.deal_count, 0),
    '100.0%'
  ]);

  return convertToCSV({ headers, rows });
}

/**
 * Convert product category data to CSV
 */
export function productCategoryToCSV(categoryData: any): string {
  if (!categoryData || !categoryData.categories) {
    return '';
  }

  const headers = ['Category', 'Funding Amount', 'Deal Count', 'Share %'];
  const rows: (string | number)[][] = [];

  const totalFunding = categoryData.categories.reduce(
    (sum: number, cat: any) => sum + cat.amount,
    0
  );
  const totalDeals = categoryData.categories.reduce(
    (sum: number, cat: any) => sum + cat.count,
    0
  );

  categoryData.categories.forEach((category: any) => {
    rows.push([
      category.name,
      formatCurrency(category.amount),
      category.count,
      `${category.percentage.toFixed(1)}%`
    ]);
  });

  // Add total row
  rows.push([
    'Total',
    formatCurrency(totalFunding),
    totalDeals,
    '100.0%'
  ]);

  return convertToCSV({ headers, rows });
}

/**
 * Convert acquisition category data to CSV
 */
export function acquisitionCategoryToCSV(categoryData: any): string {
  if (!categoryData || !categoryData.categories) {
    return '';
  }

  const headers = ['Category', 'Acquisition Count', 'Companies'];
  const rows: (string | number)[][] = [];

  const totalAcquisitions = categoryData.categories.reduce(
    (sum: number, cat: any) => sum + cat.count,
    0
  );

  categoryData.categories.forEach((category: any) => {
    rows.push([
      category.name,
      category.count,
      category.companies || ''
    ]);
  });

  // Add total row
  rows.push([
    'Total',
    totalAcquisitions,
    ''
  ]);

  return convertToCSV({ headers, rows });
}

/**
 * Convert comparison chart data to CSV
 */
export function comparisonChartToCSV(
  chartData: V2ChartData,
  chartTitle: string,
  chartType: 'funding' | 'ma'
): string {
  const headers = ['Period', ...chartData.series.map(s => s.name)];
  const rows: (string | number)[][] = [];

  // Add chart info
  rows.push([`=== ${chartTitle} ===`, '']);
  rows.push([`Type: ${chartType === 'funding' ? 'Funding Trends' : 'M&A Activity'}`, '']);
  rows.push(['', '']);

  // Add data rows
  chartData.x_axis.forEach((label, index) => {
    const row: (string | number)[] = [label];
    chartData.series.forEach(series => {
      const value = series.data[index] || 0;
      row.push(chartType === 'funding' ? formatCurrency(value) : value);
    });
    rows.push(row);
  });

  // Add totals row
  const totalsRow: (string | number)[] = ['Total'];
  chartData.series.forEach(series => {
    const total = series.data.reduce((sum, val) => sum + val, 0);
    totalsRow.push(chartType === 'funding' ? formatCurrency(total) : total);
  });
  rows.push(totalsRow);

  // Add comparison analysis if two series
  if (chartData.series.length === 2) {
    rows.push(['', '']);
    rows.push(['=== Comparison Analysis ===', '']);

    const series1Total = chartData.series[0].data.reduce((sum, val) => sum + val, 0);
    const series2Total = chartData.series[1].data.reduce((sum, val) => sum + val, 0);
    const difference = series2Total - series1Total;
    const percentDiff = series1Total > 0 ? ((difference / series1Total) * 100).toFixed(1) : '0.0';

    rows.push([
      `${chartData.series[1].name} vs ${chartData.series[0].name}`,
      chartType === 'funding' ? formatCurrency(difference) : difference.toString()
    ]);
    rows.push(['Percent Difference', `${percentDiff}%`]);
  }

  return convertToCSV({ headers, rows });
}

/**
 * Convert M&A activity data to CSV
 */
export function maActivityToCSV(chartData: V2ChartData): string {
  const headers = ['Period', ...chartData.series.map(s => s.name)];
  const rows: (string | number)[][] = [];

  // Add data rows
  chartData.x_axis.forEach((label, index) => {
    const row: (string | number)[] = [label];
    chartData.series.forEach(series => {
      row.push(series.data[index] || 0);
    });
    rows.push(row);
  });

  // Add totals row
  const totalsRow: (string | number)[] = ['Total'];
  chartData.series.forEach(series => {
    const total = series.data.reduce((sum, val) => sum + val, 0);
    totalsRow.push(total);
  });
  rows.push(totalsRow);

  // Add YoY comparison if we have two years
  if (chartData.series.length === 2) {
    const yoyRow: (string | number)[] = ['YoY Change'];
    const series2024 = chartData.series.find(s => s.name === '2024');
    const series2025 = chartData.series.find(s => s.name === '2025');

    if (series2024 && series2025) {
      const total2024 = series2024.data.reduce((sum, val) => sum + val, 0);
      const total2025 = series2025.data.reduce((sum, val) => sum + val, 0);
      const yoyChange = total2024 > 0 ? ((total2025 - total2024) / total2024) * 100 : 0;
      yoyRow.push('');
      yoyRow.push(`${yoyChange.toFixed(1)}%`);
      rows.push(yoyRow);
    }
  }

  return convertToCSV({ headers, rows });
}

/**
 * Convert generic data to CSV format
 */
function convertToCSV(data: ExportData): string {
  const { headers, rows } = data;

  // Create CSV content
  const csvRows = [];

  // Add headers
  csvRows.push(headers.map(h => escapeCSV(h)).join(','));

  // Add data rows
  rows.forEach(row => {
    csvRows.push(row.map(cell => escapeCSV(cell)).join(','));
  });

  return csvRows.join('\n');
}

/**
 * Escape CSV values
 */
function escapeCSV(value: string | number): string {
  if (typeof value === 'number') {
    return value.toString();
  }

  // Escape quotes and wrap in quotes if contains comma, newline, or quotes
  if (value.includes(',') || value.includes('\n') || value.includes('"')) {
    return `"${value.replace(/"/g, '""')}"`;
  }

  return value;
}

/**
 * Format currency values
 */
function formatCurrency(value: number): string {
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  } else if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  } else if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

/**
 * Download CSV file
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Download data as Excel-compatible CSV
 */
export function downloadExcelCSV(csvContent: string, filename: string): void {
  // Add BOM for Excel to recognize UTF-8
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.replace('.csv', '.csv'); // Ensure .csv extension
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Generate filename with timestamp
 */
export function generateExportFilename(prefix: string, extension: string): string {
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 10); // YYYY-MM-DD
  return `${prefix}_${timestamp}.${extension}`;
}

/**
 * Generate analytical export filename
 */
export function generateAnalyticalFilename(analysisType: string, timeGrouping: string): string {
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 10);
  return `cybersecurity-${analysisType}-${timeGrouping}-${timestamp}.csv`;
}

/**
 * Export multiple datasets to a single Excel-compatible CSV
 */
export function exportCombinedData(datasets: { name: string; csv: string }[]): string {
  const combined: string[] = [];

  datasets.forEach((dataset, index) => {
    // Add dataset name as header
    combined.push(`=== ${dataset.name.toUpperCase()} ===`);
    combined.push('');
    combined.push(dataset.csv);

    // Add spacing between datasets
    if (index < datasets.length - 1) {
      combined.push('');
      combined.push('');
    }
  });

  return combined.join('\n');
}

/**
 * Export filtered aggregated data to CSV
 */
export function filteredDataToCSV(
  data: AggregatedDataPoint[],
  activeFilters: AdvancedFilters,
  timeGrouping: string,
  chartType: 'funding' | 'ma'
): string {
  const headers: string[] = ['Period', 'Display Label'];

  if (chartType === 'funding') {
    headers.push('2024 Funding', '2025 Funding', 'YoY Growth %');
    if (data.some(d => d.dealCounts2024 || d.dealCounts2025)) {
      headers.push('2024 Deals', '2025 Deals', 'Deal Growth %');
    }
  } else {
    headers.push('2024 M&A Deals', '2025 M&A Deals', 'YoY Growth %');
  }

  const rows: (string | number)[][] = [];

  // Add filter info header
  const filterInfo: string[] = [];
  if (activeFilters.valueRange.enabled) {
    filterInfo.push(`Value Range: $${activeFilters.valueRange.min}M - $${activeFilters.valueRange.max}M`);
  }
  if (activeFilters.growthFilter.enabled) {
    filterInfo.push(`Growth Filter: ${activeFilters.growthFilter.type} (${activeFilters.growthFilter.threshold}%)`);
  }
  if (activeFilters.dealVolumeFilter.enabled) {
    filterInfo.push(`Deal Volume: ${activeFilters.dealVolumeFilter.minDeals} - ${activeFilters.dealVolumeFilter.maxDeals}`);
  }
  if (activeFilters.dataQuality.enabled) {
    filterInfo.push(`Data Quality: Min $${activeFilters.dataQuality.minValue}M${activeFilters.dataQuality.requireBothYears ? ', Both Years Required' : ''}`);
  }

  if (filterInfo.length > 0) {
    rows.push(['=== APPLIED FILTERS ===', '']);
    filterInfo.forEach(filter => rows.push([filter, '']));
    rows.push(['', '']);
  }

  rows.push(['=== FILTERED DATA ===', '']);
  rows.push([`Time Grouping: ${timeGrouping}`, `Chart Type: ${chartType}`]);
  rows.push(['', '']);

  // Add data rows
  data.forEach(point => {
    const row: (string | number)[] = [point.period, point.label];

    if (chartType === 'funding') {
      row.push(
        formatCurrency(point.data2024),
        formatCurrency(point.data2025)
      );

      // Calculate YoY growth
      const yoyGrowth = point.data2024 > 0
        ? ((point.data2025 - point.data2024) / point.data2024) * 100
        : 0;
      row.push(`${yoyGrowth.toFixed(1)}%`);

      // Add deal counts if available
      if (point.dealCounts2024 !== undefined || point.dealCounts2025 !== undefined) {
        row.push(
          point.dealCounts2024 || 0,
          point.dealCounts2025 || 0
        );

        const dealGrowth = (point.dealCounts2024 || 0) > 0
          ? (((point.dealCounts2025 || 0) - (point.dealCounts2024 || 0)) / (point.dealCounts2024 || 0)) * 100
          : 0;
        row.push(`${dealGrowth.toFixed(1)}%`);
      }
    } else {
      row.push(
        point.data2024,
        point.data2025
      );

      const yoyGrowth = point.data2024 > 0
        ? ((point.data2025 - point.data2024) / point.data2024) * 100
        : 0;
      row.push(`${yoyGrowth.toFixed(1)}%`);
    }

    rows.push(row);
  });

  // Add summary statistics
  if (data.length > 0) {
    rows.push(['', '']);
    rows.push(['=== SUMMARY ===', '']);
    rows.push(['Total Periods', data.length.toString()]);

    const total2024 = data.reduce((sum, d) => sum + d.data2024, 0);
    const total2025 = data.reduce((sum, d) => sum + d.data2025, 0);
    const overallGrowth = total2024 > 0 ? ((total2025 - total2024) / total2024) * 100 : 0;

    if (chartType === 'funding') {
      rows.push(['2024 Total', formatCurrency(total2024)]);
      rows.push(['2025 Total', formatCurrency(total2025)]);
    } else {
      rows.push(['2024 Total Deals', total2024.toString()]);
      rows.push(['2025 Total Deals', total2025.toString()]);
    }
    rows.push(['Overall YoY Growth', `${overallGrowth.toFixed(1)}%`]);
  }

  return convertToCSV({ headers, rows });
}

/**
 * Export statistical analysis to CSV
 */
export function statisticalAnalysisToCSV(
  fundingStats: StatisticalMetrics,
  maStats: StatisticalMetrics,
  timeGrouping: string
): string {
  const headers = ['Metric', 'Funding Trends', 'M&A Activity', 'Unit'];
  const rows: (string | number)[][] = [];

  rows.push(['=== STATISTICAL ANALYSIS ===', '', '', '']);
  rows.push([`Time Grouping: ${timeGrouping}`, '', '', '']);
  rows.push(['', '', '', '']);

  // Basic statistics
  rows.push(['=== CENTRAL TENDENCY ===', '', '', '']);
  rows.push(['Mean', formatCurrency(fundingStats.mean), maStats.mean.toFixed(1), 'Currency / Deals']);
  rows.push(['Median', formatCurrency(fundingStats.median), maStats.median.toFixed(1), 'Currency / Deals']);
  rows.push(['Min', formatCurrency(fundingStats.min), maStats.min.toFixed(1), 'Currency / Deals']);
  rows.push(['Max', formatCurrency(fundingStats.max), maStats.max.toFixed(1), 'Currency / Deals']);
  rows.push(['', '', '', '']);

  // Variability
  rows.push(['=== VARIABILITY ===', '', '', '']);
  rows.push(['Standard Deviation', formatCurrency(fundingStats.stdDev), maStats.stdDev.toFixed(2), 'Currency / Deals']);
  rows.push(['Variance', fundingStats.variance.toFixed(2), maStats.variance.toFixed(2), 'Squared Units']);
  rows.push(['Volatility Score', `${fundingStats.volatilityScore.toFixed(1)}%`, `${maStats.volatilityScore.toFixed(1)}%`, 'Coefficient of Variation']);
  rows.push(['', '', '', '']);

  // Growth metrics
  rows.push(['=== GROWTH ANALYSIS ===', '', '', '']);
  rows.push(['Total Growth', `${fundingStats.totalGrowth.toFixed(1)}%`, `${maStats.totalGrowth.toFixed(1)}%`, 'Cumulative %']);
  rows.push(['Average Growth Rate', `${fundingStats.avgGrowthRate.toFixed(1)}%`, `${maStats.avgGrowthRate.toFixed(1)}%`, 'Average %']);
  rows.push(['', '', '', '']);

  // Risk assessment
  rows.push(['=== RISK ASSESSMENT ===', '', '', '']);
  const fundingRisk = fundingStats.volatilityScore > 50 ? 'High' : fundingStats.volatilityScore > 30 ? 'Medium' : 'Low';
  const maRisk = maStats.volatilityScore > 50 ? 'High' : maStats.volatilityScore > 30 ? 'Medium' : 'Low';
  rows.push(['Risk Level', fundingRisk, maRisk, 'Qualitative']);

  const fundingStability = fundingStats.volatilityScore < 25 ? 'Stable' : fundingStats.volatilityScore < 50 ? 'Moderate' : 'Volatile';
  const maStability = maStats.volatilityScore < 25 ? 'Stable' : maStats.volatilityScore < 50 ? 'Moderate' : 'Volatile';
  rows.push(['Market Stability', fundingStability, maStability, 'Qualitative']);

  return convertToCSV({ headers, rows });
}

/**
 * Export trend analysis to CSV
 */
export function trendAnalysisToCSV(
  fundingTrends: TrendAnalysis,
  maTrends: TrendAnalysis,
  timeGrouping: string
): string {
  const headers = ['Metric', 'Funding Trends', 'M&A Activity', 'Details'];
  const rows: (string | number)[][] = [];

  rows.push(['=== TREND ANALYSIS ===', '', '', '']);
  rows.push([`Time Grouping: ${timeGrouping}`, '', '', '']);
  rows.push(['', '', '', '']);

  // Trend direction
  rows.push(['=== MARKET DIRECTION ===', '', '', '']);
  rows.push(['Trend Direction', fundingTrends.direction, maTrends.direction, 'bullish/bearish/sideways']);
  rows.push(['Trend Strength', `${fundingTrends.strength.toFixed(1)}%`, `${maTrends.strength.toFixed(1)}%`, '0-100% scale']);
  rows.push(['Momentum', `${fundingTrends.momentum.toFixed(1)}%`, `${maTrends.momentum.toFixed(1)}%`, 'Rate of change']);
  rows.push(['', '', '', '']);

  // Predictions
  rows.push(['=== PREDICTIONS ===', '', '', '']);
  rows.push(['Next Period Prediction',
    formatCurrency(fundingTrends.prediction.nextPeriod),
    `${maTrends.prediction.nextPeriod.toFixed(0)} deals`,
    'Predicted value']);
  rows.push(['Prediction Direction',
    fundingTrends.prediction.direction,
    maTrends.prediction.direction,
    'up/down/stable']);
  rows.push(['Confidence Level',
    `${fundingTrends.prediction.confidence.toFixed(0)}%`,
    `${maTrends.prediction.confidence.toFixed(0)}%`,
    'Prediction reliability']);
  rows.push(['', '', '', '']);

  // Seasonality
  rows.push(['=== SEASONALITY ===', '', '', '']);
  rows.push(['Pattern Detected',
    fundingTrends.seasonality.detected ? 'Yes' : 'No',
    maTrends.seasonality.detected ? 'Yes' : 'No',
    'Seasonal patterns']);

  if (fundingTrends.seasonality.detected || maTrends.seasonality.detected) {
    rows.push(['Pattern Description',
      fundingTrends.seasonality.pattern,
      maTrends.seasonality.pattern,
      'Pattern details']);
    rows.push(['Pattern Confidence',
      `${fundingTrends.seasonality.confidence.toFixed(0)}%`,
      `${maTrends.seasonality.confidence.toFixed(0)}%`,
      'Pattern reliability']);
  }
  rows.push(['', '', '', '']);

  // Market outlook
  rows.push(['=== MARKET OUTLOOK ===', '', '', '']);
  const overallTrend = (fundingTrends.direction === 'bullish' && maTrends.direction === 'bullish') ? 'Strong Bull' :
                      (fundingTrends.direction === 'bearish' && maTrends.direction === 'bearish') ? 'Bear Market' :
                      (fundingTrends.direction === 'bullish' || maTrends.direction === 'bullish') ? 'Mixed Bull' : 'Neutral';

  const marketSync = fundingTrends.direction === maTrends.direction ? 'Aligned' : 'Divergent';
  const predictionQuality = Math.min(fundingTrends.prediction.confidence, maTrends.prediction.confidence) > 60 ? 'High' :
                          Math.min(fundingTrends.prediction.confidence, maTrends.prediction.confidence) > 30 ? 'Medium' : 'Low';

  rows.push(['Overall Trend', overallTrend, '', 'Combined assessment']);
  rows.push(['Market Synchronization', marketSync, '', 'Funding vs M&A alignment']);
  rows.push(['Prediction Quality', predictionQuality, '', 'Overall confidence']);

  return convertToCSV({ headers, rows });
}

/**
 * Export comprehensive analytical report
 */
export function exportAnalyticalReport(
  filteredData: AggregatedDataPoint[],
  fundingStats: StatisticalMetrics,
  maStats: StatisticalMetrics,
  fundingTrends: TrendAnalysis,
  maTrends: TrendAnalysis,
  activeFilters: AdvancedFilters,
  timeGrouping: string
): string {
  const datasets = [
    {
      name: 'Filtered Data',
      csv: filteredDataToCSV(filteredData, activeFilters, timeGrouping, 'funding')
    },
    {
      name: 'Statistical Analysis',
      csv: statisticalAnalysisToCSV(fundingStats, maStats, timeGrouping)
    },
    {
      name: 'Trend Analysis',
      csv: trendAnalysisToCSV(fundingTrends, maTrends, timeGrouping)
    }
  ];

  return exportCombinedData(datasets);
}